// src/components/share/ShareListModal.jsx
import React, { useEffect, useMemo, useState } from "react";

const ShareListModal = ({ isOpen, onClose, couple }) => {
  const [copied, setCopied] = useState(false);

  // 1) URL
  const shareUrl = useMemo(() => {
    if (!couple?.id) return "";
    return `${window.location.origin}/lista/${couple.id}`;
  }, [couple?.id]);

  // 2) Mensaje default (depende de couple + url)
  const defaultMessage = useMemo(() => {
    if (!couple) return "";
    return `¡Hola! 😊
Te comparto nuestra lista de regalos de ${couple.brideName} y ${couple.groomName}:

${shareUrl}

¡Gracias!`;
  }, [couple, shareUrl]);

  // 3) Mensaje editable
  const [message, setMessage] = useState(defaultMessage);

  // 4) Re-sincronizar cuando cambia la pareja / link
  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  const whatsappHref = useMemo(() => {
    return `https://wa.me/?text=${encodeURIComponent(message)}`;
  }, [message]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      const el = document.createElement("textarea");
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  if (!isOpen || !couple) return null;

  return (
    <div className="modal-overlay">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-xl sm:text-2xl font-display font-bold text-neutral-900">
              Compartir mi lista
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Enviá el enlace a tus invitados
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors flex items-center justify-center text-xl text-neutral-700"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Link */}
        <div className="space-y-2">
          <input
            readOnly
            value={shareUrl}
            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-mono bg-neutral-50"
          />

          <button
            onClick={copyLink}
            className="w-full rounded-lg bg-neutral-900 text-white py-2.5 text-sm font-semibold hover:bg-neutral-800 transition-colors"
          >
            {copied ? "✅ Enlace copiado" : "Copiar enlace"}
          </button>
        </div>

        <div className="my-5 border-t border-neutral-200" />

        {/* Mensaje editable */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-neutral-700">
            Mensaje para tus invitados
          </label>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent-400"
          />

          <p className="text-xs text-neutral-500">
            Podés personalizar el texto antes de compartirlo.
          </p>
        </div>

        {/* CTA WhatsApp */}
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-4 text-center rounded-lg bg-accent-500 text-white py-2.5 text-sm font-medium hover:bg-accent-600 transition-colors"
        >
          Compartir por WhatsApp
        </a>

        <p className="text-xs text-neutral-500 text-center mt-2">
          Clicá el botón y compartí tu lista por WhatsApp.
        </p>

        <button
          onClick={onClose}
          className="w-full mt-4 text-sm text-neutral-500 hover:text-neutral-700"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default ShareListModal;